import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, CalendarPlus, CreditCard, FileText, Loader2, Save, Smartphone, Trash2, UploadCloud, UserPlus, Wifi } from 'lucide-react';
import { useApiClient } from '../../lib/apiClient';
import { languageLabels, supportedLanguages, useI18n } from '../../i18n/LanguageContext';
import { SupportedLanguage } from '../../i18n/translations';

type MePayload = {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  title: string | null;
  phone: string | null;
  preferredLanguage: SupportedLanguage | null;
  timeZone: string | null;
  dateFormat: string | null;
  timeFormat: '12h' | '24h' | null;
  defaultLandingPath: string | null;
  signature: string | null;
  notificationPrefs: Record<string, unknown>;
  uiPrefs: Record<string, unknown>;
  organization: {
    id: string;
    defaultLanguage: SupportedLanguage;
    effectiveLanguage: SupportedLanguage;
  };
  mustChangePassword: boolean;
};

type FormState = {
  firstName: string;
  lastName: string;
  title: string;
  phone: string;
  preferredLanguage: SupportedLanguage | '';
  timeZone: string;
  dateFormat: string;
  timeFormat: '12h' | '24h';
  defaultLandingPath: string;
  signature: string;
  notificationPrefs: {
    emailAlerts: boolean;
    taskReminders: boolean;
    appointmentAlerts: boolean;
    billingAlerts: boolean;
  };
  uiPrefs: {
    compactMode: boolean;
    signatureImageUrl: string;
    signatureImageKey: string;
  };
};

const ME_REQUEST_DEDUPE_MS = 5000;
let meCache: MePayload | null = null;
let meCacheTimestamp = 0;
let meRequestInFlight: Promise<MePayload> | null = null;

const defaultForm: FormState = {
  firstName: '',
  lastName: '',
  title: '',
  phone: '',
  preferredLanguage: '',
  timeZone: 'UTC',
  dateFormat: 'MM/dd/yyyy',
  timeFormat: '12h',
  defaultLandingPath: '/appointments',
  signature: '',
  notificationPrefs: {
    emailAlerts: true,
    taskReminders: true,
    appointmentAlerts: true,
    billingAlerts: true,
  },
  uiPrefs: {
    compactMode: false,
    signatureImageUrl: '',
    signatureImageKey: '',
  },
};

function toBool(value: unknown, fallback: boolean) {
  if (typeof value === 'boolean') return value;
  return fallback;
}

function validatePasswordPolicy(nextPassword: string) {
  const value = String(nextPassword || '');
  if (value.length < 12) return 'Password must be at least 12 characters long.';
  if (!/[A-Z]/.test(value)) return 'Password must include at least one uppercase letter.';
  if (!/[a-z]/.test(value)) return 'Password must include at least one lowercase letter.';
  if (!/[0-9]/.test(value)) return 'Password must include at least one number.';
  if (!/[^A-Za-z0-9]/.test(value)) return 'Password must include at least one symbol.';
  return null;
}

function formatPasswordApiError(payload: unknown, fallback: string) {
  if (!payload || typeof payload !== 'object') return fallback;
  const body = payload as {
    message?: unknown;
    error?: unknown;
    issues?: Array<{ path?: Array<string | number>; message?: unknown }>;
    validation?: Array<{ field?: unknown; message?: unknown }>;
  };

  const messages: string[] = [];
  if (typeof body.message === 'string' && body.message.trim()) {
    messages.push(body.message.trim());
  }
  if (Array.isArray(body.issues)) {
    for (const issue of body.issues) {
      const issueMessage = typeof issue.message === 'string' ? issue.message.trim() : '';
      if (!issueMessage) continue;
      const path = Array.isArray(issue.path) && issue.path.length ? issue.path.join('.') : '';
      messages.push(path ? `${path}: ${issueMessage}` : issueMessage);
    }
  }
  if (Array.isArray(body.validation)) {
    for (const item of body.validation) {
      const itemMessage = typeof item.message === 'string' ? item.message.trim() : '';
      if (!itemMessage) continue;
      const field = typeof item.field === 'string' && item.field.trim() ? item.field.trim() : '';
      messages.push(field ? `${field}: ${itemMessage}` : itemMessage);
    }
  }
  if (typeof body.error === 'string' && body.error.trim()) {
    messages.push(body.error.trim());
  }

  return messages.length ? Array.from(new Set(messages)).join(' ') : fallback;
}

async function getMe(fetchJson: ReturnType<typeof useApiClient>['fetchJson'], force = false) {
  const now = Date.now();
  if (!force && meCache && now - meCacheTimestamp < ME_REQUEST_DEDUPE_MS) {
    return meCache;
  }
  if (meRequestInFlight) {
    return meRequestInFlight;
  }

  const request = fetchJson<MePayload>('/users/me');
  meRequestInFlight = request;
  try {
    const me = await request;
    meCache = me;
    meCacheTimestamp = Date.now();
    return me;
  } finally {
    if (meRequestInFlight === request) {
      meRequestInFlight = null;
    }
  }
}

function updateMeCache(me: MePayload) {
  meCache = me;
  meCacheTimestamp = Date.now();
}

export default function SettingsMyAccount() {
  const navigate = useNavigate();
  const location = useLocation();
  const { fetchApi, fetchJson } = useApiClient();
  const { t, tr, setLanguage, orgDefaultLanguage, effectiveLanguage, refresh } = useI18n();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [banner, setBanner] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [signatureUploadError, setSignatureUploadError] = useState<string | null>(null);
  const [uploadingSignature, setUploadingSignature] = useState(false);
  const [email, setEmail] = useState('');
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordBanner, setPasswordBanner] = useState<string | null>(null);
  const [passwordInputsArmed, setPasswordInputsArmed] = useState(false);
  const [form, setForm] = useState<FormState>(defaultForm);
  const [mobileSyncOnline, setMobileSyncOnline] = useState(true);
  const [mobilePermissionSetup, setMobilePermissionSetup] = useState({
    camera: true,
    notifications: true,
    calendarLock: false,
    tapToPay: true,
  });
  const securitySectionRef = useRef<HTMLElement | null>(null);
  const currentPasswordRef = useRef<HTMLInputElement | null>(null);
  const newPasswordRef = useRef<HTMLInputElement | null>(null);
  const confirmPasswordRef = useRef<HTMLInputElement | null>(null);
  const forcePasswordChange = mustChangePassword || location.search.includes('forcePasswordChange=1');

  const resetPasswordFields = useCallback(() => {
    setPasswordForm({
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    });
    setPasswordInputsArmed(false);
    currentPasswordRef.current?.blur();
    newPasswordRef.current?.blur();
    confirmPasswordRef.current?.blur();
    if (currentPasswordRef.current) currentPasswordRef.current.value = '';
    if (newPasswordRef.current) newPasswordRef.current.value = '';
    if (confirmPasswordRef.current) confirmPasswordRef.current.value = '';
  }, []);

  const loadMe = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const me = await getMe(fetchJson);
      setEmail(me.email);
      setMustChangePassword(Boolean(me.mustChangePassword));
      const notifPrefs = (me.notificationPrefs || {}) as Record<string, unknown>;
      const uiPrefs = (me.uiPrefs || {}) as Record<string, unknown>;
      setForm({
        firstName: me.firstName || '',
        lastName: me.lastName || '',
        title: me.title || '',
        phone: me.phone || '',
        preferredLanguage: me.preferredLanguage || '',
        timeZone: me.timeZone || 'UTC',
        dateFormat: me.dateFormat || 'MM/dd/yyyy',
        timeFormat: me.timeFormat || '12h',
        defaultLandingPath: me.defaultLandingPath && me.defaultLandingPath !== '/dashboard' ? me.defaultLandingPath : '/appointments',
        signature: me.signature || '',
        notificationPrefs: {
          emailAlerts: toBool(notifPrefs.emailAlerts, true),
          taskReminders: toBool(notifPrefs.taskReminders, true),
          appointmentAlerts: toBool(notifPrefs.appointmentAlerts, true),
          billingAlerts: toBool(notifPrefs.billingAlerts, true),
        },
        uiPrefs: {
          compactMode: toBool(uiPrefs.compactMode, false),
          signatureImageUrl: typeof uiPrefs.signatureImageUrl === 'string' ? uiPrefs.signatureImageUrl : '',
          signatureImageKey: typeof uiPrefs.signatureImageKey === 'string' ? uiPrefs.signatureImageKey : '',
        },
      });
    } catch (err: any) {
      setError(err.message || 'Unable to load account profile.');
    } finally {
      setLoading(false);
    }
  }, [fetchJson]);

  useEffect(() => {
    void loadMe();
  }, [loadMe]);

  useEffect(() => {
    if (!forcePasswordChange) return;
    securitySectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [forcePasswordChange]);

  useEffect(() => {
    resetPasswordFields();
    if (!forcePasswordChange) {
      return;
    }
    setPasswordError(null);
    setPasswordBanner(null);
  }, [forcePasswordChange, resetPasswordFields]);

  useEffect(() => {
    const clearAutofill = () => {
      resetPasswordFields();
    };
    const frame = window.requestAnimationFrame(clearAutofill);
    const timeout = window.setTimeout(clearAutofill, 150);
    return () => {
      window.cancelAnimationFrame(frame);
      window.clearTimeout(timeout);
    };
  }, [resetPasswordFields]);

  async function save() {
    setSaving(true);
    setError(null);
    setBanner(null);
    try {
      const updatedMe = await fetchJson<MePayload>('/users/me', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
          title: form.title.trim() || null,
          phone: form.phone.trim() || null,
          preferredLanguage: form.preferredLanguage || null,
          timeZone: form.timeZone.trim() || null,
          dateFormat: form.dateFormat.trim() || null,
          timeFormat: form.timeFormat || null,
          defaultLandingPath: form.defaultLandingPath.trim() || null,
          signature: form.signature.trim() || null,
          notificationPrefs: form.notificationPrefs,
          uiPrefs: form.uiPrefs,
        }),
      });
      updateMeCache(updatedMe);
      await refresh();
      const preferred = form.preferredLanguage || orgDefaultLanguage;
      setLanguage(preferred);
      setBanner(t('account.saved', 'Profile updated successfully.'));
    } catch (err: any) {
      setError(err.message || 'Unable to save account profile.');
    } finally {
      setSaving(false);
    }
  }

  async function updatePassword() {
    setPasswordSaving(true);
    setPasswordError(null);
    setPasswordBanner(null);

    if (!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
      setPasswordError(tr('Enter your current password and type the new password twice.'));
      setPasswordSaving(false);
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError(t('account.passwordMismatch', 'New password and confirmation do not match.'));
      setPasswordSaving(false);
      return;
    }

    const policyError = validatePasswordPolicy(passwordForm.newPassword);
    if (policyError) {
      setPasswordError(tr(policyError));
      setPasswordSaving(false);
      return;
    }

    try {
      const res = await fetchApi('/auth/change-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          currentPassword: passwordForm.currentPassword,
          newPassword: passwordForm.newPassword,
        }),
      });
      if (!res.ok) {
        const payload = await res.json().catch(() => null);
        throw new Error(formatPasswordApiError(payload, 'Unable to update password.'));
      }
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
      setPasswordInputsArmed(false);
      setMustChangePassword(false);
      setPasswordBanner(t('account.passwordUpdated', 'Password updated successfully.'));
      const refreshed = await getMe(fetchJson, true);
      updateMeCache(refreshed);
      if (location.search.includes('forcePasswordChange=1')) {
        navigate('/settings/account', { replace: true });
      }
    } catch (err: any) {
      setPasswordError(err?.message ? tr(err.message) : t('account.passwordUpdateFailed', 'Unable to update password.'));
    } finally {
      setPasswordSaving(false);
    }
  }

  async function uploadSignatureImage(file?: File) {
    if (!file) return;
    setSignatureUploadError(null);

    const allowedTypes = ['image/png', 'image/jpeg'];
    if (!allowedTypes.includes(file.type)) {
      setSignatureUploadError('Signature image must be a PNG or JPEG file.');
      return;
    }
    if (file.size > 3 * 1024 * 1024) {
      setSignatureUploadError('Signature image must be 3MB or smaller.');
      return;
    }

    setUploadingSignature(true);
    try {
      const presign = await fetchJson<{ key: string; uploadUrl: string; url: string }>('/storage/presign', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fileName: file.name,
          contentType: file.type,
          kind: 'doctor-signature',
        }),
      });

      const uploadResponse = await fetch(presign.uploadUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': file.type,
        },
        body: file,
      });
      if (!uploadResponse.ok) {
        throw new Error(`Upload failed (${uploadResponse.status})`);
      }

      setForm((prev) => ({
        ...prev,
        uiPrefs: {
          ...prev.uiPrefs,
          signatureImageUrl: presign.url,
          signatureImageKey: presign.key,
        },
      }));
    } catch (err: any) {
      setSignatureUploadError(err?.message || 'Signature upload failed.');
    } finally {
      setUploadingSignature(false);
    }
  }

  if (loading) return <div className="p-6">{t('common.loading', 'Loading...')}</div>;

  return (
    <div className="space-y-6 pb-10">
      <div className="flex items-center justify-between sticky top-0 bg-slate-50/80 backdrop-blur-sm py-4 z-10 border-b border-slate-200/50 -mx-6 px-6">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate('/settings')} className="p-2 hover:bg-slate-200 rounded-full transition-colors">
            <ArrowLeft size={20} className="text-slate-600" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-ink">{t('account.title', 'My Account')}</h1>
            <p className="text-slate-500 text-sm">{t('account.subtitle', 'Manage personal profile, language, and user preferences.')}</p>
          </div>
        </div>
        <button
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-ink text-white shadow-lg shadow-ink/20 hover:bg-ink/90 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
          onClick={save}
          disabled={saving || forcePasswordChange}
        >
          <Save size={18} /> {saving ? `${t('common.save', 'Save')}...` : t('common.save', 'Save')}
        </button>
      </div>

      {banner && <div className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2">{banner}</div>}
      {error && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-100 rounded-lg px-3 py-2">{error}</div>}
      {forcePasswordChange ? (
        <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          {t('account.passwordRequired', 'Password change is required before continuing.')}
        </div>
      ) : null}

      <div className="card p-6 space-y-8 max-w-5xl">
        <section className={`space-y-4 ${forcePasswordChange ? 'opacity-35 blur-[2px] pointer-events-none select-none' : ''}`}>
          <h3 className="text-lg font-semibold text-ink">{t('account.personal', 'Personal Information')}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input label={t('account.firstName', 'First Name')} value={form.firstName} onChange={(value) => setForm((prev) => ({ ...prev, firstName: value }))} />
            <Input label={t('account.lastName', 'Last Name')} value={form.lastName} onChange={(value) => setForm((prev) => ({ ...prev, lastName: value }))} />
            <Input label={t('account.titleField', 'Professional Title')} value={form.title} onChange={(value) => setForm((prev) => ({ ...prev, title: value }))} />
            <Input label={t('account.phone', 'Phone')} value={form.phone} onChange={(value) => setForm((prev) => ({ ...prev, phone: value }))} />
            <Input label={t('account.email', 'Email')} value={email} onChange={() => {}} disabled />
          </div>
        </section>

        <section className={`space-y-4 border-t border-slate-100 pt-4 ${forcePasswordChange ? 'opacity-35 blur-[2px] pointer-events-none select-none' : ''}`}>
          <h3 className="text-lg font-semibold text-ink">{t('account.preferences', 'Locale & App Preferences')}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label className="text-sm space-y-1 block">
              <span className="text-slate-500">{t('account.language', 'Preferred Language')}</span>
              <select
                className="w-full border border-slate-200 rounded-lg px-3 py-2 bg-white"
                value={form.preferredLanguage}
                onChange={(event) => setForm((prev) => ({ ...prev, preferredLanguage: event.target.value as SupportedLanguage | '' }))}
              >
                <option value="">Use organization default ({languageLabels[orgDefaultLanguage]})</option>
                {supportedLanguages.map((language) => (
                  <option key={language} value={language}>
                    {languageLabels[language]}
                  </option>
                ))}
              </select>
            </label>
            <Input label={t('account.timezone', 'Time Zone')} value={form.timeZone} onChange={(value) => setForm((prev) => ({ ...prev, timeZone: value }))} />
            <Input label={t('account.dateFormat', 'Date Format')} value={form.dateFormat} onChange={(value) => setForm((prev) => ({ ...prev, dateFormat: value }))} />
            <label className="text-sm space-y-1 block">
              <span className="text-slate-500">{t('account.timeFormat', 'Time Format')}</span>
              <select className="w-full border border-slate-200 rounded-lg px-3 py-2 bg-white" value={form.timeFormat} onChange={(event) => setForm((prev) => ({ ...prev, timeFormat: event.target.value as '12h' | '24h' }))}>
                <option value="12h">12h</option>
                <option value="24h">24h</option>
              </select>
            </label>
            <Input label={t('account.defaultLanding', 'Default Landing Page')} value={form.defaultLandingPath} onChange={(value) => setForm((prev) => ({ ...prev, defaultLandingPath: value }))} />
          </div>
          <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
            {t('account.orgLanguage', 'Organization Default Language')}: <strong>{languageLabels[orgDefaultLanguage]}</strong> •{' '}
            {t('account.effectiveLanguage', 'Effective App Language')}: <strong>{languageLabels[effectiveLanguage]}</strong>
          </div>
        </section>

        <section className={`space-y-4 border-t border-slate-100 pt-4 ${forcePasswordChange ? 'opacity-35 blur-[2px] pointer-events-none select-none' : ''}`}>
          <h3 className="text-lg font-semibold text-ink">{t('account.notifications', 'Notifications')}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
            <Toggle label={tr('Email alerts')} checked={form.notificationPrefs.emailAlerts} onChange={(checked) => setForm((prev) => ({ ...prev, notificationPrefs: { ...prev.notificationPrefs, emailAlerts: checked } }))} />
            <Toggle label={tr('Task reminders')} checked={form.notificationPrefs.taskReminders} onChange={(checked) => setForm((prev) => ({ ...prev, notificationPrefs: { ...prev.notificationPrefs, taskReminders: checked } }))} />
            <Toggle label={tr('Appointment alerts')} checked={form.notificationPrefs.appointmentAlerts} onChange={(checked) => setForm((prev) => ({ ...prev, notificationPrefs: { ...prev.notificationPrefs, appointmentAlerts: checked } }))} />
            <Toggle label={tr('Billing alerts')} checked={form.notificationPrefs.billingAlerts} onChange={(checked) => setForm((prev) => ({ ...prev, notificationPrefs: { ...prev.notificationPrefs, billingAlerts: checked } }))} />
            <Toggle label={tr('Compact mode')} checked={form.uiPrefs.compactMode} onChange={(checked) => setForm((prev) => ({ ...prev, uiPrefs: { ...prev.uiPrefs, compactMode: checked } }))} />
          </div>
        </section>

        <section ref={securitySectionRef} className={`space-y-4 border-t border-slate-100 pt-4 rounded-xl ${forcePasswordChange ? 'ring-2 ring-amber-400 bg-amber-50/50 p-4 -mx-2' : ''}`}>
          <h3 className="text-lg font-semibold text-ink">
            {tr('Security')} {forcePasswordChange ? <span className="ml-2 text-xs font-medium text-amber-700 align-middle">{tr('Action required')}</span> : null}
          </h3>
          {passwordBanner && <div className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2">{passwordBanner}</div>}
          {passwordError && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-100 rounded-lg px-3 py-2">{passwordError}</div>}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <label className="text-sm space-y-1 block">
              <span className="text-slate-500">{tr('Current Password')}</span>
              <input
                ref={currentPasswordRef}
                name="account-current-password"
                autoComplete="off"
                type="password"
                readOnly={!passwordInputsArmed}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 bg-white"
                value={passwordForm.currentPassword}
                onPointerDown={() => setPasswordInputsArmed(true)}
                onFocus={() => setPasswordInputsArmed(true)}
                onChange={(event) => setPasswordForm((prev) => ({ ...prev, currentPassword: event.target.value }))}
              />
            </label>
            <label className="text-sm space-y-1 block">
              <span className="text-slate-500">{tr('New Password')}</span>
              <input
                ref={newPasswordRef}
                name="account-new-password"
                autoComplete="off"
                type="password"
                readOnly={!passwordInputsArmed}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 bg-white"
                value={passwordForm.newPassword}
                onPointerDown={() => setPasswordInputsArmed(true)}
                onFocus={() => setPasswordInputsArmed(true)}
                onChange={(event) => setPasswordForm((prev) => ({ ...prev, newPassword: event.target.value }))}
              />
            </label>
            <label className="text-sm space-y-1 block">
              <span className="text-slate-500">{tr('Confirm New Password')}</span>
              <input
                ref={confirmPasswordRef}
                name="account-confirm-password"
                autoComplete="off"
                type="password"
                readOnly={!passwordInputsArmed}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 bg-white"
                value={passwordForm.confirmPassword}
                onPointerDown={() => setPasswordInputsArmed(true)}
                onFocus={() => setPasswordInputsArmed(true)}
                onChange={(event) => setPasswordForm((prev) => ({ ...prev, confirmPassword: event.target.value }))}
              />
            </label>
          </div>
          <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
            {tr('Password guidance: use at least 12 characters with uppercase, lowercase, a number, and a symbol.')} {tr('Do not reuse your current password.')}
          </div>
          <div>
            <button type="button" className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-ink text-white shadow-lg shadow-ink/20 hover:bg-ink/90 transition-all disabled:opacity-60 disabled:cursor-not-allowed" onClick={updatePassword} disabled={passwordSaving}>
              {passwordSaving ? `${t('common.save', 'Save')}...` : tr('Update Password')}
            </button>
          </div>
        </section>

        <section className={`space-y-4 border-t border-slate-100 pt-4 ${forcePasswordChange ? 'opacity-35 blur-[2px] pointer-events-none select-none' : ''}`}>
          <div className="flex items-center gap-2">
            <Smartphone className="h-5 w-5 text-ink" />
            <h3 className="text-lg font-semibold text-ink">Pabau Go / Mobile</h3>
          </div>
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
            <MobileCard icon={<Smartphone size={18} />} title="App setup & permissions">
              {Object.entries(mobilePermissionSetup).map(([key, value]) => (
                <label key={key} className="flex items-center justify-between rounded-lg border border-slate-100 bg-slate-50 px-3 py-2 text-sm">
                  <span className="capitalize">{key.replace(/([A-Z])/g, ' $1')}</span>
                  <input
                    type="checkbox"
                    checked={value}
                    onChange={(event) => setMobilePermissionSetup((current) => ({ ...current, [key]: event.target.checked }))}
                    className="accent-ink"
                  />
                </label>
              ))}
            </MobileCard>

            <MobileCard icon={<CalendarPlus size={18} />} title="Appointments & book-outs">
              <div className="grid grid-cols-2 gap-2 text-xs">
                {['Create appointment', 'Edit appointment', 'Reschedule', 'Cancel', 'Create book-out', 'Manage room'].map((item) => (
                  <button key={item} type="button" className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-slate-700 hover:bg-slate-50">
                    {item}
                  </button>
                ))}
              </div>
            </MobileCard>

            <MobileCard icon={<UserPlus size={18} />} title="Clients, leads & quotes">
              <div className="grid grid-cols-2 gap-2 text-xs">
                {['Create client', 'Upload client photo', 'Before/after compare', 'Lead follow-up', 'Quote approval', 'Mobile sync'].map((item) => (
                  <div key={item} className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">{item}</div>
                ))}
              </div>
            </MobileCard>

            <MobileCard icon={<FileText size={18} />} title="Forms completion">
              <div className="rounded-lg border border-slate-100 bg-slate-50 p-3 text-sm">
                Complete, edit, and submit assigned medical forms from the mobile journey.
              </div>
            </MobileCard>

            <MobileCard icon={<CreditCard size={18} />} title="Mobile checkout">
              <div className="grid grid-cols-2 gap-2 text-xs">
                {['Sale / checkout', 'Tap to Pay', 'Payment link', 'Receipt email'].map((item) => (
                  <div key={item} className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">{item}</div>
                ))}
              </div>
            </MobileCard>

            <MobileCard icon={<Wifi size={18} />} title="Data sync status">
              <label className="flex items-center justify-between rounded-lg border border-slate-100 bg-slate-50 px-3 py-2 text-sm">
                <span>{mobileSyncOnline ? 'Online sync active' : 'Offline mode'}</span>
                <input type="checkbox" checked={mobileSyncOnline} onChange={(event) => setMobileSyncOnline(event.target.checked)} className="accent-ink" />
              </label>
              <div className="rounded-lg border border-emerald-100 bg-emerald-50 p-3 text-sm text-emerald-800">Last synced 2 minutes ago. Queue: 0 pending uploads.</div>
            </MobileCard>
          </div>
        </section>

        <section className={`space-y-4 border-t border-slate-100 pt-4 ${forcePasswordChange ? 'opacity-35 blur-[2px] pointer-events-none select-none' : ''}`}>
          <h3 className="text-lg font-semibold text-ink">{t('account.signature', 'Clinical Signature')}</h3>
          <textarea className="w-full border border-slate-200 rounded-lg px-3 py-2 bg-white h-28 text-sm resize-none" value={form.signature} onChange={(event) => setForm((prev) => ({ ...prev, signature: event.target.value }))} placeholder="Clinical signature or credentials block..." />
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <label className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm cursor-pointer hover:bg-slate-50">
                {uploadingSignature ? <Loader2 size={14} className="animate-spin" /> : <UploadCloud size={14} />}
                {uploadingSignature ? 'Uploading...' : 'Upload Signature Image'}
                <input
                  type="file"
                  className="hidden"
                  accept="image/png,image/jpeg"
                  onChange={(event) => {
                    const selected = event.target.files?.[0];
                    event.currentTarget.value = '';
                    uploadSignatureImage(selected);
                  }}
                />
              </label>
              {form.uiPrefs.signatureImageUrl && (
                <button
                  type="button"
                  onClick={() =>
                    setForm((prev) => ({
                      ...prev,
                      uiPrefs: {
                        ...prev.uiPrefs,
                        signatureImageUrl: '',
                        signatureImageKey: '',
                      },
                    }))
                  }
                  className="inline-flex items-center gap-1 px-3 py-2 rounded-lg border border-rose-200 bg-rose-50 text-sm text-rose-700 hover:bg-rose-100"
                >
                  <Trash2 size={14} />
                  Remove
                </button>
              )}
            </div>
            {signatureUploadError && <p className="text-xs text-rose-600">{signatureUploadError}</p>}
            {form.uiPrefs.signatureImageUrl && (
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                <img src={form.uiPrefs.signatureImageUrl} alt="Doctor signature" className="h-20 w-auto object-contain" />
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}

function Input({
  label,
  value,
  onChange,
  disabled = false,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}) {
  return (
    <label className="text-sm space-y-1 block">
      <span className="text-slate-500">{label}</span>
      <input
        className="w-full border border-slate-200 rounded-lg px-3 py-2 bg-white disabled:bg-slate-100 disabled:text-slate-500"
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
      />
    </label>
  );
}

function Toggle({ label, checked, onChange }: { label: string; checked: boolean; onChange: (value: boolean) => void }) {
  return (
    <label className="inline-flex items-center gap-2 text-sm text-slate-700">
      <input type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} />
      {label}
    </label>
  );
}

function MobileCard({ icon, title, children }: { icon: ReactNode; title: string; children: ReactNode }) {
  return (
    <section className="space-y-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-center gap-2 text-ink">
        {icon}
        <h4 className="text-sm font-semibold">{title}</h4>
      </div>
      {children}
    </section>
  );
}
